import React from 'react';
import { Outlet, Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import {
  Box, Flex, HStack, Button, Menu, MenuButton, MenuList, MenuItem,
  IconButton, useDisclosure, Drawer, DrawerOverlay, DrawerContent,
  DrawerCloseButton, DrawerHeader, DrawerBody, VStack, Text, Divider, Image
} from '@chakra-ui/react';
import {
  HamburgerIcon, ChevronDownIcon, CalendarIcon, CheckCircleIcon,
  SettingsIcon, AtSignIcon, LockIcon
} from '@chakra-ui/icons';
import { authState } from '../../state/authState';
import { authService } from '../../services/api';
import packageJson from '../../../package.json';

export const Layout = () => {
  const [auth, setAuth] = useRecoilState(authState);
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch (err) {
      console.error('Logout error:', err);
    }
    setAuth({ user: null, isAuthenticated: false, isLoading: false });
    navigate('/login');
  };

  const navItems = [
    { label: 'Calculator', path: '/calculator', icon: CalendarIcon },
    { label: 'Assessments', path: '/assessments', icon: CheckCircleIcon }
  ];

  const adminItems = [
    { label: 'System Dashboard', path: '/admin', icon: SettingsIcon },
    { label: 'User Management', path: '/admin/users', icon: AtSignIcon },
    { label: 'Calculation Settings', path: '/admin/settings', icon: SettingsIcon }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <Box minH="100vh">
      <Box as="header" bg="white" borderBottom="1px" borderColor="gray.200" px={4} py={3}>
        <Flex justify="space-between" align="center" maxW="1400px" mx="auto">
          <HStack spacing={8}>
            <Box
              as={RouterLink}
              to="/"
              _hover={{ opacity: 0.8 }}
              cursor="pointer"
            >
              <Image
                src="/img/iadc-logo.png"
                alt="IADC"
                h="28px"
              />
            </Box>

            <HStack spacing={3} display={{ base: 'none', md: 'flex' }}>
              {navItems.map(item => (
                <Button
                  key={item.path}
                  as={RouterLink}
                  to={item.path}
                  variant={isActive(item.path) ? 'solid' : 'ghost'}
                  size="sm"
                  leftIcon={<item.icon />}
                >
                  {item.label}
                </Button>
              ))}

              {['super_admin', 'manager'].includes(auth.user?.role) && (
                <Menu>
                  <MenuButton
                    as={Button}
                    rightIcon={<ChevronDownIcon />}
                    leftIcon={<SettingsIcon />}
                    variant={location.pathname.startsWith('/admin') ? 'solid' : 'ghost'}
                    size="sm"
                  >
                    Admin
                  </MenuButton>
                  <MenuList>
                    {adminItems.map(item => (
                      <MenuItem key={item.path} as={RouterLink} to={item.path} icon={<item.icon />}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              )}
            </HStack>
          </HStack>

          <HStack spacing={3}>
            <Text display={{ base: 'none', sm: 'block' }} fontSize="sm" color="gray.600">
              {auth.user?.email}
            </Text>
            <Button variant="outline" size="sm" onClick={handleLogout} display={{ base: 'none', md: 'flex' }} leftIcon={<LockIcon />}>
              Logout
            </Button>
            <IconButton
              icon={<HamburgerIcon />}
              variant="ghost"
              display={{ base: 'flex', md: 'none' }}
              onClick={onOpen}
              aria-label="Open menu"
            />
          </HStack>
        </Flex>
      </Box>

      <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <Image
              src="/img/iadc-logo.png"
              alt="IADC"
              h="28px"
            />
          </DrawerHeader>
          <DrawerBody>
            <VStack spacing={3} align="stretch" mt={4}>
              {navItems.map(item => (
                <Button
                  key={item.path}
                  as={RouterLink}
                  to={item.path}
                  variant={isActive(item.path) ? 'solid' : 'ghost'}
                  onClick={onClose}
                  justifyContent="flex-start"
                  leftIcon={<item.icon />}
                >
                  {item.label}
                </Button>
              ))}

              {['super_admin', 'manager'].includes(auth.user?.role) && (
                <>
                  <Divider />
                  <Text fontWeight="bold" color="gray.500" fontSize="sm">Admin</Text>
                  {adminItems.map(item => (
                    <Button
                      key={item.path}
                      as={RouterLink}
                      to={item.path}
                      variant={isActive(item.path) ? 'solid' : 'ghost'}
                      onClick={onClose}
                      justifyContent="flex-start"
                      leftIcon={<item.icon />}
                    >
                      {item.label}
                    </Button>
                  ))}
                </>
              )}

              <Divider />
              <Text fontSize="sm" color="gray.600">{auth.user?.email}</Text>
              <Button variant="outline" onClick={handleLogout} leftIcon={<LockIcon />}>
                Logout
              </Button>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <Box as="main" maxW="1400px" mx="auto" p={4}>
        <Outlet />
      </Box>

      <Box as="footer" py={1} maxW="1400px" mx="auto" px={4} textAlign="right">
        <Text fontSize="xs" color="gray.400">v{packageJson.version}</Text>
      </Box>
    </Box>
  );
};

export default Layout;
